import { action, computed, makeObservable, observable } from 'mobx';

import { trackEvent } from '@/utils/analytics';

import { HIGH_SCORE_STORAGE_KEY } from './bird-game.constants';

export type ViewPhase = 'closed' | 'flying-in' | 'open' | 'flying-out';
export type GameStatus = 'idle' | 'playing' | 'paused' | 'gameOver';

export type Point = { x: number; y: number };

function loadHighScore(): number {
  if (typeof window === 'undefined') return 0;
  const stored = Number(localStorage.getItem(HIGH_SCORE_STORAGE_KEY));
  return Number.isFinite(stored) && stored > 0 ? stored : 0;
}

class BirdGameStore {
  phase: ViewPhase = 'closed';
  status: GameStatus = 'idle';
  score = 0;
  highScore = loadHighScore();
  returnFrom: Point | null = null;
  private summoned = false;

  constructor() {
    makeObservable(this, {
      phase: observable,
      status: observable,
      score: observable,
      highScore: observable,
      returnFrom: observable.ref,
      isOpen: computed,
      summon: action,
      enter: action,
      exit: action,
      finishFlightIn: action,
      leave: action,
      beginRun: action,
      setScore: action,
      endRun: action,
      pauseRun: action,
      resumeRun: action,
    });
  }

  get isOpen(): boolean {
    return this.phase !== 'closed';
  }

  summon(): void {
    if (this.isOpen) return;
    this.summoned = true;
  }

  enter(): void {
    this.phase = this.summoned ? 'flying-in' : 'open';
    this.status = 'idle';
    this.score = 0;
    this.returnFrom = null;
    trackEvent('DESKTOP_BIRD_GAME_OPENED');
  }

  exit(): void {
    this.phase = 'closed';
    this.summoned = false;
  }

  finishFlightIn(): void {
    if (this.phase !== 'flying-in') return;
    this.phase = 'open';
  }

  leave(returnFrom: Point | null): void {
    if (this.phase !== 'open') return;
    this.phase = 'flying-out';
    this.status = 'idle';
    this.returnFrom = returnFrom;
  }

  beginRun(): void {
    this.status = 'playing';
    this.score = 0;
    trackEvent('DESKTOP_BIRD_GAME_STARTED');
  }

  setScore(score: number): void {
    this.score = score;
  }

  endRun(finalScore: number): void {
    this.status = 'gameOver';
    this.score = finalScore;
    if (finalScore > this.highScore) {
      this.highScore = finalScore;
      localStorage.setItem(HIGH_SCORE_STORAGE_KEY, String(finalScore));
    }
    trackEvent('DESKTOP_BIRD_GAME_OVER', { score: finalScore });
  }

  pauseRun(): void {
    if (this.status !== 'playing') return;
    this.status = 'paused';
  }

  resumeRun(): void {
    if (this.status !== 'paused') return;
    this.status = 'playing';
  }
}

export const birdGameStore = new BirdGameStore();
